import { useCallback, useEffect, useRef, useState } from "react";

import { IntlProvider } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { ufoEventOnboardingInviteUsers } from "@/components/signup/utils/ufo-events";
import { useLocale } from "@/libraries/web-framework/src/localization/use-locale";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";

import { ANALYTICS_PREFIX } from "../../../../onboarding-wizard/common/constants";
import { LeftHandSidePageInvite } from "../../../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/left-hand-side/left-hand-side";
import { StepContext, StepProps } from "../../../types";
import { Header } from "../../components/header";

export const Invite = <T extends StepContext>({
    context,
    setContext,
    nextStep,
    onSubmit
}: StepProps<T>) => {
    const { teamType, template } = context;
    const locale = useLocale();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const onSubmitFired = useRef(false);
    const [invitePromiseStored, setInvitePromiseStored] = useState(false);

    const experiment = useExperiment(
        experiments.jfindOnboardingWizardInviteStep
    );

    const isEnrolledInInviteTreatment =
        experiment.type === "enrolled" && experiment.value === true;

    useEffect(() => {
        if (isEnrolledInInviteTreatment) {
            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: `${ANALYTICS_PREFIX}InviteStep`,
                attributes: {
                    teamType,
                    template
                }
            }).fire();
        }
    }, [createAnalyticsEvent, teamType, template, isEnrolledInInviteTreatment]);

    useEffect(() => {
        if (experiment.type === "enrolled") {
            experiment.fireExposure();
            ufoEventOnboardingInviteUsers.start();
        }

        if (!isEnrolledInInviteTreatment) {
            nextStep();
        }
    }, [experiment, isEnrolledInInviteTreatment, nextStep]);

    useEffect(() => {
        if (
            onSubmit &&
            !onSubmitFired.current &&
            context.emailList &&
            context.emailList.length > 0
        ) {
            // submit bulk email invites
            const action = () => onSubmit({ context, setContext });

            // mark invites as sent
            onSubmitFired.current = true;

            // store promise to listen for completion on loading step
            setContext((prevContext) => ({
                ...prevContext,
                promisesToResolve: prevContext.promisesToResolve.concat([
                    action
                ])
            }));

            setInvitePromiseStored(true);
        }
    }, [context, onSubmit, setContext, nextStep]);

    useEffect(() => {
        // move to next step once invite promise is stored.
        if (invitePromiseStored) {
            nextStep();
        }
    }, [nextStep, invitePromiseStored]);

    const onStepComplete = useCallback(
        async (emailList: string[] = []) => {
            // go to next step if there are no emails to invite
            if (emailList.length === 0) {
                nextStep();
                return;
            }

            setContext((prevContext) => ({
                ...prevContext,
                emailList
            }));
        },
        [nextStep, setContext]
    );

    // skip invite step if not in treatment
    if (!isEnrolledInInviteTreatment) {
        return null;
    }

    return (
        <IntlProvider locale={locale}>
            <LeftHandSidePageInvite onSubmit={onStepComplete} Header={Header} />
        </IntlProvider>
    );
};
